import Vue from 'vue'
import { Module } from 'vuex'
import desc from '@/helpers/desc'
import ProductImage from '@/types/ProductImage'
import Notification from '@/types/Notification'
import PresetImage from './helper/PresetImage'
import PlatformModel from '@/types/Platform'
import * as platforms from './platform-settings'
import { ImageRule } from '@/types/definitions/ImageRule'
import { PlatformType } from '@/types/definitions/PlatformType'
import { PlatformImage } from '@/types/definitions/PlatformImage'
import { StoredProductType } from '@/types/definitions/StoredProductType'

export type PlatformsState = {
	platforms: { [key: string]: PlatformType }
}

/**
 * Whether the rule places the measurements image, as opposed to using the
 * same photo for something else.
 */
function isMeasurementsRule(rule?: Partial<ImageRule> | null) {
	return !!(
		rule &&
		rule.category &&
		rule.category[0] === 'special' &&
		rule.category[1] === 'measurements'
	)
}

const getPlatformState = () => ({
	shopify: platforms.Shopify,
	ebay: platforms.Ebay,
	'ebay-unbranded': platforms.EbayUnbranded,
	'e-k-v2': platforms.Kleinanzeigen,
	'used-design': platforms.UsedDesign,
	pamono: platforms.Pamono,
	vinterior: platforms.Vinterior,
	google: platforms.Google,
	facebook: platforms.Facebook
})

export default {
	namespaced: true,

	state: {
		platforms: getPlatformState()
	},

	getters: {
		byId(state: PlatformsState) {
			return (id: string) => state.platforms[id]
		},

		image(state: PlatformsState) {
			return (id: string, index: number) =>
				state.platforms[id].images[index]
		},

		platforms(state: PlatformsState) {
			return Object.values(state.platforms)
		},

		data(state: PlatformsState) {
			return Object.values(state.platforms).map(p => p.data)
		},

		storables(state: PlatformsState) {
			return Object.values(state.platforms).map(p => p.storable)
		},

		selectedImages(state: PlatformsState) {
			const selectedImages = [] as {
				platform: string
				ix: number
				image: PlatformImage
			}[]

			Object.keys(state.platforms).map(platform =>
				state.platforms[platform].images.forEach(
					(i, ix) =>
						i &&
						i.selected &&
						selectedImages.push({ platform, ix, image: i })
				)
			)

			return selectedImages
		},

		hasSelectedImages(_state, getters) {
			return !!getters.selectedImages.length
		}
	},

	mutations: {
		UPDATE_PLATFORM(
			state: PlatformsState,
			platform: {
				id: PlatformType['id']
				[key: string]: any
			}
		) {
			Object.keys(platform).forEach(key => {
				Vue.set(state.platforms[platform.id], key, platform[key])
			})
		},

		SET_IMAGE(
			state: PlatformsState,
			data: {
				id: PlatformType['id']
				data?: PlatformImage | null
				ix: number
			}
		) {
			if (data.data) {
				Vue.set(state.platforms[data.id].images, data.ix, data.data)
			} else {
				Vue.delete(state.platforms[data.id].images, data.ix)
			}
		},

		CLEAR(state) {
			Object.keys(state.platforms).forEach(platformId => {
				state.platforms[platformId].images
					.map((_i, key) => key)
					.sort(desc)
					.forEach(i =>
						Vue.delete(state.platforms[platformId].images, i)
					)
			})
		}
	},

	actions: {
		LOAD(
			{ commit, dispatch, state, rootGetters },
			{ platforms, images }: StoredProductType
		) {
			const isSet = rootGetters.product.type === 'set'
			const slotImage = rootGetters['images/byCategoryAndSubCategory'](
				'special',
				'measurements'
			)[0] as ProductImage | undefined
			const presetImage =
				slotImage instanceof PresetImage ? slotImage : undefined

			// Names of the platforms whose saved measurements image was swapped
			const swappedToPreset: string[] = []
			let presetChanged = false

			const resolveImage = (
				original: string,
				rule: Partial<ImageRule>,
				platformName: string
			): ProductImage | undefined => {
				const image = rootGetters['images/byId'](original) as
					| ProductImage
					| undefined

				// An older version of a preset image, or one that lost the
				// slot to a photo tagged by hand. Sets have no measurements
				// slot, so their children's preset images are skipped.
				if (!image && PresetImage.isPresetId(original)) {
					if (isSet || !slotImage) {
						return undefined
					}

					presetChanged = true

					return slotImage
				}

				// Saved before preset images existed: a photo that was picked
				// automatically for the measurements slot gives way to the
				// preset image.
				if (
					presetImage &&
					image &&
					image.id !== presetImage.id &&
					!image.categorizedManually &&
					isMeasurementsRule(rule)
				) {
					if (!swappedToPreset.includes(platformName)) {
						swappedToPreset.push(platformName)
					}

					return presetImage
				}

				return image
			}

			const loaded = Promise.all(
				Object.keys(platforms).map(async id => {
					const platformImages =
						images[id] ||
						([] as Array<
							PlatformImage & {
								order: number
								original: string
							}
						>)

					const imageList = Array(
						state.platforms[id].settings.maxCount
					).map(() => ({}))

					platformImages.forEach(i => {
						let rule = i.rule || {}

						if (typeof rule === 'string') {
							rule = JSON.parse(rule as string) as ImageRule
						}

						rule.overlays = rule.overlays

						const image = resolveImage(
							i.original,
							rule,
							state.platforms[id].settings.name
						)

						if (!image) {
							return
						}

						imageList[i.order] = {
							image,
							rule: rule,
							selected: false
						}
					})

					commit(
						'UPDATE_PLATFORM',
						Object.assign({ id }, platforms[id], {
							images: []
						})
					)

					imageList.forEach((img, ix) => {
						commit('SET_IMAGE', { id, ix, data: img })
					})
				})
			)

			return loaded.then(result => {
				if (presetChanged) {
					dispatch(
						'notifications/ADD',
						new Notification(
							'Preset measurement image changed since last save, please save again.',
							'warning'
						),
						{ root: true }
					)
				}

				if (presetImage && swappedToPreset.length) {
					dispatch(
						'notifications/ADD',
						new Notification(
							`The measurements image on ${swappedToPreset.join(
								', '
							)} was replaced with the measurement image of preset "${
								presetImage.presetName
							}". Please check these images and save.`,
							'warning'
						),
						{ root: true }
					)
				}

				return result
			})
		},

		ADD_IMAGE(
			{ rootGetters, getters, commit },
			imageData: { id: PlatformType['id']; image: ProductImage['id'] }
		) {
			const p = getters.byId(imageData.id)

			if (p) {
				const ix = p.images.reduce(
					(c: number, i: PlatformImage | {}, ix: number) =>
						Object.keys(i).length ? c : Math.min(c, ix),
					p.images.length
				)

				const image = rootGetters['images/byId'](imageData.image)

				commit('SET_IMAGE', {
					ix,
					id: imageData.id,
					data: {
						image,
						rule: {
							allowFullPicture: image.autoCategory === 'detail',
							padding: {
								top: image.autoCategory === 'detail' ? 0 : 0.3,
								bottom:
									image.autoCategory === 'detail' ? 0 : 0.3
							},
							custom: true,
							overlays: p.settings.defaultOverlays || []
						},
						selected: false
					}
				})
			}
		},

		/**
		 * Puts the new image into every platform slot where the previous image
		 * is used as the measurements image. Slots that use the previous image
		 * for anything else are left alone.
		 */
		REPLACE_MEASUREMENTS_IMAGE(
			{ state, commit, rootGetters },
			{
				previousImageId,
				newImageId
			}: {
				previousImageId: ProductImage['id']
				newImageId: ProductImage['id']
			}
		) {
			const newImage = rootGetters['images/byId'](newImageId)

			if (!newImage) {
				return
			}

			Object.values(state.platforms).forEach(platformEntry => {
				const platform = platformEntry as PlatformModel
				let updated = false

				platform.images.forEach((existing, index) => {
					if (
						existing &&
						existing.image &&
						existing.image.id === previousImageId &&
						isMeasurementsRule(existing.rule)
					) {
						commit('SET_IMAGE', {
							id: platform.id,
							ix: index,
							data: {
								image: newImage,
								rule: existing.rule,
								selected: false
							}
						})

						updated = true
					}
				})

				if (updated) {
					platform.resetCustom()
				}
			})
		},

		RESET_IMAGE_OCCURRENCES_FOR_IMAGE(
			{ state, commit, rootState },
			{
				previousImageId,
				newImageId: _newImageId
			}: {
				previousImageId: ProductImage['id']
				newImageId?: ProductImage['id']
			}
		) {
			if (!previousImageId) {
				return
			}

			const root = rootState as {
				product?: { product?: { type?: string } }
			}
			const productType = root.product?.product?.type
			const isSet = productType === 'set'

			Object.values(state.platforms).forEach(platformEntry => {
				const platform = platformEntry as PlatformModel
				const rules =
					isSet && platform.settings.setRules
						? platform.settings.setRules
						: platform.settings.rules
				const autoImages = platform.getAutoImages(rules, isSet)
				let updated = false

				platform.images.forEach((existing, index) => {
					if (
						existing &&
						existing.image &&
						existing.image.id === previousImageId
					) {
						const replacement = autoImages[index] || null

						commit('SET_IMAGE', {
							id: platform.id,
							ix: index,
							data: replacement || null
						})

						updated = true
					}
				})

				if (updated) {
					platform.resetCustom()
				}
			})
		},

		REMOVE_IMAGE(
			{ getters, commit },
			imageData: { id: PlatformType['id']; index: number }
		) {
			const p = getters.byId(imageData.id)

			if (p) {
				commit('SET_IMAGE', {
					ix: imageData.index,
					id: imageData.id
				})
			}
		},

		CLEAR_PLATFORM(
			{ getters, commit, state },
			Data: { id: PlatformType['id'] }
		) {
			const p = getters.byId(Data.id)

			if (p) {
				state.platforms[Data.id].images
					.map((_i, key) => key)
					.sort(desc)
					.forEach(i =>
						Vue.delete(state.platforms[Data.id].images, i)
					)
			}
		},

		EDIT_IMAGE(
			{ getters, commit },
			imageData: {
				id: PlatformType['id']
				index: keyof PlatformType['images']
				rule: ImageRule
			}
		) {
			const p = getters.byId(imageData.id)

			if (p) {
				const image = getters.image(imageData.id, imageData.index)

				image.rule = Object.assign({}, imageData.rule, { custom: true })

				commit('SET_IMAGE', {
					ix: imageData.index,
					id: imageData.id,
					data: image
				})
			}
		}
	}
} as Module<PlatformsState, unknown>
